/**
 * I passi del tutorial guidato dell'elenco Categorie.
 *
 * E' la pagina con piu' preparativi finora: due menu della barra in alto, il menu
 * della riga, il cassetto dell'ordinamento e - per far comparire la barra delle
 * azioni multiple - una riga da spuntare. Ogni passo prepara quello che gli serve
 * e `stopCategoriesListTour` rimette tutto com'era, spunta compresa.
 *
 * I bersagli sono scoped sulla pagina: la scheda categoria vive nella dialog di
 * questa stessa pagina e ha un tutorial suo con gli stessi nomi di bersaglio.
 */
const at = name => `[data-tour-scope="categories-list"] [data-tour="${name}"]`;

/**
 * Le voci dei menu si cercano senza scope: Vuetify stacca il contenuto dei menu e
 * lo appende a `[data-app]`, fuori dalla pagina. I nomi sono scelti perche' non si
 * ripetano altrove.
 */
const ADD_CATEGORY = '[data-tour="add-category"]';
const ADD_FILE = '[data-tour="add-file"]';
const EXPORT_CSV = '[data-tour="export-csv"]';
const ROW_EDIT = '[data-tour="row-edit"]';

const SETTLE_TIMEOUT_MS = 1500;
const POLL_MS = 50;
const STILL_MS = 250;

/** Vero quando e' stato il giro ad aprire il cassetto o a spuntare la riga. */
let openedDrawer = false;

/**
 * L'elemento, ma solo se e' davvero dentro la finestra.
 *
 * Si guarda il centro e non il bordo: da chiuso il cassetto lascia dentro la
 * finestra qualche pixel di se stesso, abbastanza da far passare un controllo sul
 * bordo e da convincere il tutorial che sia gia' aperto.
 */
function onScreen(selector) {
	return () => {
		const element = document.querySelector(selector);

		if (!element || element.getClientRects().length === 0) {
			return null;
		}

		const content = element.closest('.v-menu__content');

		/* Un menu in chiusura resta a schermo, ma perde subito la classe di attivo. */
		if (content && !content.classList.contains('menuable__content__active')) {
			return null;
		}

		const box = element.getBoundingClientRect();
		const centerX = box.left + box.width / 2;

		if (centerX < 0 || centerX > window.innerWidth) {
			return null;
		}

		return element;
	};
}

function isOnScreen(selector) {
	return onScreen(selector)() !== null;
}

function position(selector) {
	const element = onScreen(selector)();

	if (!element) {
		return null;
	}

	const box = element.getBoundingClientRect();

	return `${Math.round(box.top)}:${Math.round(box.left)}`;
}

async function waitStable(selector) {
	const deadline = performance.now() + SETTLE_TIMEOUT_MS;
	const needed = Math.ceil(STILL_MS / POLL_MS);
	let previous = null;
	let still = 0;

	while (performance.now() < deadline) {
		await new Promise(resolve => setTimeout(resolve, POLL_MS));

		const current = position(selector);

		still = current !== null && current === previous ? still + 1 : 0;
		previous = current;

		if (still >= needed) {
			return;
		}
	}
}

/**
 * Lascia scattare la chiusura che Vuetify ha gia' in coda.
 *
 * I menu si chiudono al primo click fuori, e il click su "Avanti" e' fuori: la
 * direttiva l'ha registrato in cattura, prima ancora che il pulsante facesse il
 * suo lavoro, e ha accodato la chiusura. Riaprendo subito, il menu si
 * richiuderebbe un istante dopo.
 */
function afterPendingClose() {
	return new Promise(resolve => setTimeout(resolve, 0));
}

function click(selector) {
	const element = document.querySelector(selector);

	if (element) {
		element.click();
	}
}

async function openMenu(activator, probe) {
	await afterPendingClose();

	if (isOnScreen(probe)) {
		return;
	}

	const element = document.querySelector(activator);

	// Niente attivatore: non c'e' nulla da aprire e non ha senso aspettare.
	if (!element) {
		return;
	}

	element.click();
	await waitStable(probe);
}

async function closeMenus() {
	await afterPendingClose();

	// Un click sul velo del tutorial non c'e': si chiudono riaprendo l'attivatore,
	// che fa da interruttore.
	if (isOnScreen(ADD_CATEGORY)) {
		click(at('add'));
	}

	if (isOnScreen(EXPORT_CSV)) {
		click(at('export'));
	}

	if (isOnScreen(ROW_EDIT)) {
		click(at('row-menu'));
	}
}

const openAddMenu = () => openMenu(at('add'), ADD_CATEGORY);
const openExportMenu = () => openMenu(at('export'), EXPORT_CSV);
const openRowMenu = () => openMenu(at('row-menu'), ROW_EDIT);

async function openSortDrawer() {
	await closeMenus();

	if (isOnScreen(at('sort-tree'))) {
		return;
	}

	openedDrawer = true;
	click(at('sort'));
	await waitStable(at('sort-tree'));
}


/** Rimette la pagina com'era: menu chiusi, cassetto solo se l'ha aperto il giro. */
export async function stopCategoriesListTour() {
	await closeMenus();

	if (openedDrawer) {
		openedDrawer = false;
		click(at('sort'));
	}
}

export function createCategoriesListTourSteps() {
	return [
		{
			body: "Le categorie sono l'albero con cui i prodotti si trovano sul sito: ogni categoria puo' stare sotto un'altra, e un prodotto puo' stare in piu' categorie insieme. Da questa pagina le si crea, si spengono, si riordinano e si vede a colpo d'occhio quanti prodotti ha dentro ciascuna.",
			key: 'intro',
			placement: 'bottom',
			target: at('tutorial'),
			title: 'A cosa serve questa pagina',
		},
		{
			body: "Cerca nel nome della categoria, nella lingua in cui e' scritta. Funziona da quattro caratteri in su: sotto, la ricerca parte ma viene ignorata e l'elenco resta intero. Si conferma con Invio o con la lente.",
			before: closeMenus,
			key: 'search',
			placement: 'bottom',
			target: at('search'),
			title: 'Cercare una categoria',
		},
		{
			body: "Questo non e' un secondo campo di ricerca: scegliendo una categoria l'elenco si restringe a quella e a tutto quello che ci sta sotto. E' il modo per lavorare su un ramo dell'albero alla volta senza perdersi fra centinaia di voci.",
			key: 'parent',
			placement: 'bottom',
			target: at('parent'),
			title: 'Filtrare per ramo',
		},
		{
			body: "Le colonne dicono quando la categoria e' stata creata, il suo Id, il nome e - la piu' utile - Reference, cioe' quanti prodotti sono collegati a quella categoria in questo momento. Una categoria con Reference a zero e' vuota: sul sito non porta da nessuna parte.",
			key: 'table',
			placement: 'top',
			target: at('table'),
			timeout: 250,
			title: 'Come si legge la tabella',
		},
		{
			body: "Una bandierina per ogni lingua del negozio: a colori dove il nome della categoria e' scritto, in grigio dove manca. Se scrivi il nome in una sola lingua, al salvataggio vale anche per le altre lasciate vuote, slug compreso.",
			key: 'langs',
			placement: 'bottom',
			target: at('langs'),
			timeout: 250,
			title: 'Le lingue della categoria',
		},
		{
			body: "L'interruttore accende e spegne la categoria, e il cambio parte subito: non c'e' un salvataggio da confermare. Spegnerla non cancella niente e non tocca i prodotti, li smette solo di mostrare sotto quella voce. La categoria radice non ha interruttore: non si tocca.",
			key: 'status',
			placement: 'right',
			target: at('status'),
			timeout: 250,
			title: 'Accendere e spegnere',
		},
		{
			body: "I tre pallini a inizio riga aprono due voci: Modifica apre la pagina della categoria - nome, testo, immagini, dove sta nell'albero - ed Elimina la cancella, chiedendo conferma. Attenzione a eliminare una categoria con Reference alto: sono prodotti che restano senza quella collocazione.",
			before: openRowMenu,
			key: 'row-edit',
			placement: 'right',
			target: ROW_EDIT,
			timeout: 250,
			title: 'Il menu della riga',
		},
		{
			body: "Il piu' in alto apre le tre vie per aggiungere categorie. La prima, Aggiungi categoria, apre la pagina della nuova categoria: e' quella che userai quasi sempre, e ha un tutorial tutto suo.",
			before: openAddMenu,
			key: 'add-category',
			placement: 'left',
			target: ADD_CATEGORY,
			title: 'Aggiungere una categoria',
		},
		{
			body: "Importa da file esterno serve quando le categorie da creare sono tante e le hai gia' scritte altrove: si carica il file e si crea l'albero in un colpo solo, invece di aprire la scheda decine di volte. Se hai Shopify o Magento collegati, sotto compaiono anche le voci per importare da li'.",
			before: openAddMenu,
			key: 'add-file',
			placement: 'left',
			target: ADD_FILE,
			timeout: 250,
			title: 'Importare da un file',
		},
		{
			body: "La freccia in giu' scarica l'elenco, in CSV o in Excel. Rispetta quello che stai guardando: se hai un filtro attivo, scarica solo quelle categorie. Il file non nasce all'istante - viene preparato in coda e ti arriva con una mail e una notifica - quindi non serve restare ad aspettare.",
			before: openExportMenu,
			key: 'export-csv',
			placement: 'left',
			target: EXPORT_CSV,
			title: 'Scaricare l elenco',
		},
		{
			body: "Questo apre il pannello dell'ordinamento, a destra. Serve per una cosa che dalla tabella non si puo' fare: decidere in che ordine le categorie si presentano sul sito, e come sono annidate una dentro l'altra.",
			before: closeMenus,
			key: 'sort',
			placement: 'bottom',
			target: at('sort'),
			title: "Il pannello dell'ordinamento",
		},
		{
			body: "Qui l'albero si trascina: si sposta una categoria sopra o sotto le sorelle per cambiarne l'ordine, e dentro un'altra per cambiarle il genitore. Il numero fra parentesi dice quante figlie ha. Niente di quello che sposti e' salvato finche' non premi Conferma ordinamento in fondo al pannello - e finche' non lo premi, il sito continua a mostrare l'ordine di prima.",
			before: openSortDrawer,
			key: 'sort-tree',
			placement: 'left',
			target: onScreen(at('sort-tree')),
			title: "Riordinare l'albero",
		},
	];
}
